// Query tree of the platform query language (mirrors backend easydb/query/ast.py).
// Nodes carry a client-only `_id` for React keys; it is stripped before sending.

export type Match = 'fulltext' | 'phrase' | 'token' | 'wildcard'
export type Value = string | number | (string | number)[] | { from?: number; to?: number } | Record<string, unknown> | null

export type Condition = { _id?: string; field: string; value: Value; label?: string | null; match?: Match | null; not?: boolean }
export type Raw = { _id?: string; easydb: Record<string, unknown>; not?: boolean }
export type Group = { _id?: string; op: 'and' | 'or'; not?: boolean; items: Node[]; facet?: string }
export type Node = Group | Condition | Raw

export const FIELD_LABELS: Record<string, string> = {
  text: 'Alles', titel: 'Titel', beschreibung: 'Beschreibung', person: 'Person', gesicht: 'Gesicht (KI)',
  ort: 'Ort', schlagwort: 'Schlagwort', sammlung: 'Sammlung', technik: 'Technik', fotograf: 'Fotograf/in',
  jahr: 'Jahr', aufnahmedatum: 'Aufnahmedatum', inv: 'Inventarnr.', pool: 'Pool', plattform: 'Plattform-Tag',
}
export const LINK_FIELDS = new Set(['person', 'gesicht', 'ort', 'schlagwort', 'sammlung', 'technik', 'fotograf'])
export const DATE_FIELDS = new Set(['jahr', 'aufnahmedatum'])

let seq = 0
const nid = () => `n${++seq}`

export const isGroup = (n: Node): n is Group => 'items' in n
export const isRaw = (n: Node): n is Raw => 'easydb' in n
export const isCond = (n: Node): n is Condition => 'field' in n

export function emptyTree(): Group {
  return { _id: nid(), op: 'and', items: [] }
}

export function cond(field: string, value: Value, label?: string | null, not = false): Condition {
  return { _id: nid(), field, value, label: label ?? null, not }
}

/** Give every node a client id (after receiving a tree from the server). */
export function withIds<T extends Node>(n: T): T {
  const copy = { ...n, _id: n._id ?? nid() } as T
  if (isGroup(copy)) copy.items = copy.items.map(withIds)
  return copy
}

/** Normalise the root to an AND group. */
export function asRoot(n: Group): Group {
  const g = withIds(n)
  if (g.op === 'and' && !g.not) return g
  return { _id: nid(), op: 'and', items: [g] }
}

/** Strip client-only keys before sending to the API. */
export function toWire(n: Node): Node {
  if (isGroup(n)) return { op: n.op, not: !!n.not, items: n.items.map(toWire) }
  if (isRaw(n)) return { easydb: n.easydb, not: !!n.not }
  const c: Condition = { field: n.field, value: n.value, not: !!n.not }
  if (n.label) c.label = n.label
  if (n.match) c.match = n.match
  return c
}

export function isEmptyCond(c: Condition) {
  const v = c.value
  return v === null || v === undefined || (typeof v === 'string' && !v.trim()) || (Array.isArray(v) && !v.length)
}

export function conditions(n: Node, out: Condition[] = []): Condition[] {
  if (isGroup(n)) n.items.forEach((i) => conditions(i, out))
  else if (isCond(n)) out.push(n)
  return out
}

export function activeConditions(tree: Group) {
  return conditions(tree).filter((c) => !isEmptyCond(c))
}

// --- immutable edits ---------------------------------------------------------

export function mapNode(tree: Group, id: string, fn: (n: Node) => Node | null): Group {
  const visit = (n: Node): Node | null => {
    if (n._id === id) return fn(n)
    if (isGroup(n)) {
      const items = n.items.map(visit).filter((x): x is Node => x !== null)
      // drop groups that became empty through a removal (but keep the root)
      return { ...n, items }
    }
    return n
  }
  const res = visit(tree) as Group
  return pruneEmptyGroups(res, true)
}

function pruneEmptyGroups(g: Group, root = false): Group {
  const items = g.items
    .map((i) => (isGroup(i) ? pruneEmptyGroups(i) : i))
    .filter((i) => !(isGroup(i) && i.items.length === 0))
  return root ? { ...g, items } : { ...g, items }
}

export const removeNode = (tree: Group, id: string) => mapNode(tree, id, () => null)
export const updateNode = (tree: Group, id: string, patch: Partial<Condition & Group>) =>
  mapNode(tree, id, (n) => ({ ...n, ...patch }) as Node)

export function appendTo(tree: Group, groupId: string | null, node: Node): Group {
  if (!groupId || groupId === tree._id) return { ...tree, items: [...tree.items, node] }
  return mapNode(tree, groupId, (g) => (isGroup(g) ? { ...g, items: [...g.items, node] } : g))
}

function sameValue(c: Condition, field: string, value: Value) {
  return c.field === field && JSON.stringify(c.value) === JSON.stringify(value)
}

export function findCondition(tree: Group, field: string, value: Value): Condition | undefined {
  return conditions(tree).find((c) => sameValue(c, field, value))
}

/** Facet click: add, flip negation, or remove the matching condition. */
export function toggleFacet(tree: Group, field: string, value: Value, label: string, not = false, anyMode = false): Group {
  const existing = findCondition(tree, field, value)
  if (existing) {
    if (!!existing.not === not) return removeNode(tree, existing._id!)
    return updateNode(tree, existing._id!, { not })
  }
  if (anyMode && !not) {
    const grp = tree.items.find((i) => isGroup(i) && i.facet === field) as Group | undefined
    if (grp) return appendTo(tree, grp._id!, cond(field, value, label))
    const others = tree.items.filter((i) => isCond(i) && i.field === field && !i.not)
    if (others.length) {
      const g: Group = { _id: nid(), op: 'or', items: [...others, cond(field, value, label)], facet: field }
      return { ...tree, items: [...tree.items.filter((i) => !others.includes(i)), g] }
    }
  }
  return { ...tree, items: [...tree.items, cond(field, value, label, not)] }
}

export function setYearRange(tree: Group, range: { from?: number; to?: number } | null): Group {
  const items = tree.items.filter((i) => !(isCond(i) && i.field === 'jahr' && !i.not))
  return { ...tree, items: range ? [...items, cond('jahr', range)] : items }
}

export function yearRange(tree: Group): { from?: number; to?: number } | null {
  const c = tree.items.find((i) => isCond(i) && i.field === 'jahr' && !i.not) as Condition | undefined
  if (!c || !c.value || typeof c.value !== 'object' || Array.isArray(c.value)) return null
  return c.value as { from?: number; to?: number }
}

export function valueText(c: Condition): string {
  if (c.label) return c.label
  const v = c.value
  if (v && typeof v === 'object' && !Array.isArray(v) && ('from' in v || 'to' in v)) {
    const r = v as { from?: number; to?: number }
    return r.from === r.to ? String(r.from) : `${r.from ?? ''}–${r.to ?? ''}`
  }
  if (Array.isArray(v)) return v.join(', ')
  if (typeof v === 'number' && LINK_FIELDS.has(c.field)) return `#${v}`
  return v == null ? '' : typeof v === 'object' ? JSON.stringify(v) : String(v)
}

export function countGroups(g: Group): number {
  return g.items.filter(isGroup).reduce((a, i) => a + 1 + countGroups(i as Group), 0)
}
